export const useCountdown = () => {
  const calculateTimeUnits = (difference: number) => {
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  };

  /**
   * A reusable countdown timer utility that can be used across the application
   * @param callback The function to call on each tick
   * @param interval The interval in milliseconds
   * @param immediate Whether to call the callback immediately
   * @returns An object with stop method to clear the interval
   */
  const createCountdownTimer = (
    callback: () => boolean | void,
    immediate = true,
    interval = 1000
  ) => {
    let timerId: number | null = null;

    const stop = () => {
      if (timerId !== null) {
        clearInterval(timerId);
        timerId = null;
      }
    };

    const start = () => {
      if (immediate) {
        const shouldStop = callback();
        if (shouldStop) return;
      }

      timerId = setInterval(() => {
        const shouldStop = callback();
        if (shouldStop) {
          stop();
        }
      }, interval);
    };

    start();

    return {
      stop
    };
  };

  type CountdownStateOptions = {
    target: number; // Target timestamp
    currentTime?: number; // Current timestamp, defaults to Date.now()
  };

  type CountdownStateResult = {
    shouldStop: boolean;
    timeUnits: TimeUnits | null;
  };

  /**
   * Common countdown update function that works for both DOM and reactive frameworks
   * @param options Configuration for the countdown update
   * @returns Boolean indicating if the countdown should stop and the time units remaining
   */
  const getCountdownState = (
    options: CountdownStateOptions
  ): CountdownStateResult => {
    const { target, currentTime = Date.now() } = options;

    if (isNaN(target)) {
      return { shouldStop: true, timeUnits: null };
    }

    const difference = target - currentTime;

    if (difference <= 0) {
      return { shouldStop: true, timeUnits: null };
    }

    const timeUnits = calculateTimeUnits(difference);
    return { shouldStop: false, timeUnits };
  };

  return { createCountdownTimer, getCountdownState };
};

export type TimeUnits = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};
